/**
 * "How it works": the one reference for how the game pays. Rules, scoring, XP and ranks, chests, the 2x XP
 * bonus and the profile ring, each a short panel under a sticky strip of anchors. Blockari's `/guide`, in
 * 21 Wild's DOM. It states rules and never owns one: every number comes from `ui/guide-data.ts`, which reads
 * the game's constants and runs its functions.
 */

import { cardRank, cardSuit } from '@/game/rules';
import type { CardId } from '@/game/types';
import { XP_BASE, XP_RATES, xpThreshold } from '@/game/xp';
import { CHESTS_ENABLED } from '@/core/flags';
import {
  BOOST,
  END_BONUSES,
  FIVE_CARD_COUNT,
  LOGIN_STREAK,
  MAX_BUSTS,
  RANK_SPAN,
  RARITIES,
  RARITY_WORD,
  ROUND_SECONDS,
  TARGET,
  contentsRows,
  finalOddsRow,
  levelRows,
  pileExamples,
  pileRows,
  rankRows,
  streakRows,
  timeRows,
  upgradeRows,
  xpExample,
  xpLevelRows,
  xpPartRows,
  sourceRows,
  contentsTable,
  type StackedCell,
  rankChest,
  scoreExample,
  type PileExample,
  type ScoreExample,
} from '@/ui/guide-data';
import { streakLabelOf } from '@/game/scoring';
import { formatScore } from '@/ui/hud';
import { avatarRing, createScreen, rankBadgeCard, rankClass, TopBar, type TopBarCallbacks } from '@/ui/screens';
import { chestStore } from '@/chest/chests-state';
import { CHEST_TABLE } from '@/game/chests';
import { rankFor } from '@/game/xp';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

const SUIT: Record<string, string> = { h: '♥', d: '♦', c: '♣', s: '♠' };

/** `10♥`, `A♠`. */
function glyph(card: CardId): string {
  const rank = cardRank(card);
  return `${rank === 'a' ? 'A' : rank === '10' ? '10' : rank.toUpperCase()}${SUIT[cardSuit(card)]}`;
}

function cardChip(card: CardId): HTMLElement {
  const red = cardSuit(card) === 'h' || cardSuit(card) === 'd';
  return el('span', `w21-gd-card${red ? ' w21-red' : ''}`, glyph(card));
}

/** Cells that read as numbers (a count, points, a percent, a streak) sit right-aligned, as Blockari's tables do. */
const NUMERIC = /^[+\u2212-]?[\d,.]+(%|X|x| XP)?$/;

const RARITY_COLOR: Record<string, string> = { Common: '#6ff7de', Rare: '#4fb3ff', Epic: '#b97cff', Legendary: '#ffd23f' };

/** Rarity words in a line wear their colour (Blockari's `.rarity-name`). */
function rarityNames(text: string): HTMLElement {
  const out = el('span');
  let last = 0;
  for (const m of text.matchAll(/Common|Rare|Epic|Legendary/g)) {
    out.append(document.createTextNode(text.slice(last, m.index)));
    const r = el('span', 'w21-rarity-name w21-full', m[0]);
    r.style.setProperty('--rc', RARITY_COLOR[m[0]] ?? '#fff');
    out.append(r);
    last = (m.index ?? 0) + m[0].length;
  }
  out.append(document.createTextNode(text.slice(last)));
  return out;
}

function table(head: string[], rows: (string | number | HTMLElement)[][], cls = ''): HTMLElement {
  const wrap = el('div', 'w21-hiw-scroll');
  const t = el('table', `w21-odds-table w21-hiw-table ${cls}`.trim());
  const numeric = head.map((_, c) => c > 0 && rows.length > 0 && rows.every((r) => typeof r[c] !== 'object' && NUMERIC.test(String(r[c]))));
  const thead = el('thead');
  const hr = el('tr');
  head.forEach((h, c) => hr.append(el('th', numeric[c] ? 'w21-num' : undefined, h)));
  thead.append(hr);
  const tbody = el('tbody');
  for (const row of rows) {
    const tr = el('tr');
    row.forEach((cell, c) => {
      // Long prose wraps inside its cell instead of clipping (Blockari's `td.wrap`).
      const wraps = typeof cell !== 'object' && !numeric[c] && String(cell).length > 14;
      // A short label in a table with a prose column keeps to one line ("Twenty-one"), the prose wraps beside it.
      const label = c === 0 && head.length >= 3 && String(cell).length <= 14;
      const td = el('td', numeric[c] ? 'w21-num' : wraps ? 'w21-wrap' : label ? 'w21-lbl' : undefined);
      if (typeof cell === 'object') td.append(cell);
      else td.textContent = String(cell);
      tr.append(td);
    });
    tbody.append(tr);
  }
  t.append(thead, tbody);
  wrap.append(t);
  return wrap;
}

/** A section is a panel with a head, as Blockari's `Sec`. The first paragraph is its lede. */
function section(id: string, title: string, ...body: HTMLElement[]): HTMLElement {
  return sectionWith(id, title, null, ...body);
}

function sectionWith(id: string, title: string, chip: HTMLElement | null, ...body: HTMLElement[]): HTMLElement {
  const s = el('section', 'w21-panel w21-hiw-sec');
  s.id = `w21-gd-${id}`;
  s.dataset.section = id;
  const head = el('div', 'w21-panel-head');
  head.append(el('h2', undefined, title));
  if (chip) head.append(chip);
  s.append(head, ...body);
  return s;
}

/** A numbered list whose items open in bold (Blockari's `ol.hiw-list`): "Lead. rest". */
const steps = (items: Array<[string, string]>): HTMLElement => {
  const ol = el('ol', 'w21-hiw-list');
  for (const [lead, rest] of items) {
    const li = el('li');
    li.append(el('b', undefined, `${lead}.`), document.createTextNode(` ${rest}`));
    ol.append(li);
  }
  return ol;
};

/** A bold Q&A (Blockari's `dl.hiw-qa`). */
const qa = (pairs: Array<[string, string | HTMLElement]>): HTMLElement => {
  const dl = el('dl', 'w21-hiw-qa');
  for (const [q, a] of pairs) {
    dl.append(el('dt', undefined, q));
    const dd = el('dd');
    if (typeof a === 'string') dd.textContent = a;
    else dd.append(a);
    dl.append(dd);
  }
  return dl;
};

const lede = (text: string): HTMLElement => {
  const node = el('p', 'w21-hiw-lede');
  // **bold** runs, as Blockari's lede opens ("Challenge games pay it.").
  text.split('**').forEach((part, i) => node.append(i % 2 ? el('b', undefined, part) : document.createTextNode(part)));
  return node;
};
const p = (text: string): HTMLElement => el('p', 'w21-hiw-note', text);
const sub = (text: string): HTMLElement => el('h3', 'w21-t-label w21-hiw-sub', text);
const list = (items: string[]): HTMLElement => {
  const ul = el('ul', 'w21-hiw-list');
  for (const i of items) ul.append(el('li', undefined, i));
  return ul;
};
const num = (n: number): string => formatScore(n);

function exampleRow(e: PileExample): HTMLElement {
  const row = el('div', 'w21-gd-ex');
  const cards = el('div', 'w21-gd-excards');
  for (const c of e.pile) cards.append(cardChip(c));
  cards.append(el('span', 'w21-gd-plus', '+'), cardChip(e.last));
  const out = el('div', 'w21-gd-exout');
  out.append(el('b', undefined, `${e.total}`), el('span', undefined, e.bust ? 'bust' : e.points > 0 ? `+${num(e.points)}` : 'no points'));
  row.append(cards, out, el('p', 'w21-gd-exnote', e.says));
  return row;
}

/* ------------------------------------------------------------------ sections -- */

function scoring(): HTMLElement {
  const example = streakRows();
  const ex = scoreExample();
  const tierTop = example[example.length - 1]!;
  return section(
    'scoring',
    'Scoring',
    lede('Your score is the points from clears, plus a streak bonus on top of each clear in a run, plus the bonuses when the round ends. The match level (cosmetic) adds nothing.'),
    sub('The rules'),
    p(`Four piles, one deck. Play each card onto a pile. A pile that totals exactly ${TARGET}, or reaches ${FIVE_CARD_COUNT} cards without going over, clears and pays. Go over and it busts: ${MAX_BUSTS} busts ends the round. You have ${ROUND_SECONDS / 60} minutes, and in a challenge your opponent plays the exact same deck.`),
    steps([
      ['Face cards are 10', 'An ace is 11 until that would bust the pile, then 1.'],
      ['The J\u2660 and J\u2663 are wild', 'They clear whatever pile they land on, and cancel a bust.'],
      ['A bust clears the pile for nothing', 'It also breaks your streak.'],
      ['Higher score wins a challenge', 'A tie goes to the faster finish, then the earlier submission.'],
      ['You name your own wager', 'Any whole amount of $CHAIN you can afford, from 10 up to 10,000. The 10, 25, 50 and 100 tiles are shortcuts; CUSTOM takes any other amount. Winner takes both wagers.'],
      ['Your name stays hidden', 'Until somebody takes your challenge. Then you see who took it, and they see you.'],
    ]),
    sub('What a clear pays'),
    table(['Clear', 'Points', 'When'], pileRows().map((r) => [r.label, num(r.points), r.note])),
    sub('Worked plays'),
    ...pileExamples().map(exampleRow),
    sub('Streaks'),
    p('Every scoring play in a row steps your streak up. A play that scores nothing (a plain landing, or a bust) resets it. The streak bonus is added to that play\u2019s clear.'),
    table(['Streak', 'Plays in a row', 'Bonus on that clear'], example.map((r) => [r.label, r.inARow, `+${num(r.bonus)}`])),
    p(`The ladder tops out at ${tierTop.label} and keeps paying +${num(tierTop.bonus)}.`),
    sub('When the round ends'),
    table(['Bonus', 'Points', 'When'], END_BONUSES.map((b) => [b.label, num(b.points), b.note])),
    sub('Time bonus'),
    p('Whatever is left on the clock is worth points, and fewer cards left unplayed make each second worth more: seconds left \u00d7 (10 \u2212 0.2 per card left), rounded up.'),
    table(['Seconds left', 'Cards left', 'Time bonus'], timeRows().map((r) => [r.seconds, r.cards, num(r.bonus)])),
    sub('Match levels'),
    p('The LV chip while you play is cosmetic: it pays nothing and decides nothing. A higher level with a lower score still loses.'),
    table(['Level', 'Score reached at'], levelRows().map((r) => [`LV ${r.level}`, num(r.score)])),
    sub('A worked example'),
    workedScore(ex),
    p('Computed by running those plays through the game\u2019s own scoring.'),
  );
}

/** A short round, play by play, closed by the end bonuses — with a Final score row, as Blockari's example has. */
function workedScore(ex: ScoreExample): HTMLElement {
  const wrap = el('div', 'w21-hiw-scroll');
  const t = el('table', 'w21-odds-table w21-hiw-table');
  const head = el('thead');
  const hr = el('tr');
  hr.append(el('th', undefined, 'Play'), el('th', 'w21-num', 'Points'));
  head.append(hr);
  const body = el('tbody');
  const row = (label: string, value: string, cls = '', small = ''): void => {
    const tr = el('tr', cls || undefined);
    const td = el('td', undefined, label);
    if (small) td.append(document.createTextNode(' '), el('small', undefined, `\u00b7 ${small}`));
    tr.append(td, el('td', 'w21-num', value));
    body.append(tr);
  };
  for (const r of ex.rows) row(r.what, `+${num(r.points)}`);
  row('Points during play', num(ex.base), 'w21-sum');
  row('No bust', `+${num(ex.noBust)}`);
  row('Time bonus', `+${num(ex.time)}`, '', `${ex.seconds}s left, ${ex.cardsLeft} cards left`);
  row('Final score', num(ex.total), 'w21-sum w21-total');
  t.append(head, body);
  wrap.append(t);
  return wrap;
}

function xp(): HTMLElement {
  const ex = xpExample();
  const parts = ex.parts;
  const ranks = rankRows();
  const partRows: Array<[string, number]> = [
    ['Played', parts.played], ['Score', parts.score], ['Clears', parts.clears],
    ['Levels', parts.levels], ['Streak', parts.streak], ['Challenge', parts.challenge],
  ];
  const exTable = el('div', 'w21-hiw-scroll');
  const et = el('table', 'w21-odds-table w21-hiw-table');
  const eb = el('tbody');
  for (const [label, value] of partRows) {
    if (value <= 0) continue;
    const tr = el('tr');
    tr.append(el('td', undefined, label), el('td', 'w21-num', `+${num(value)}`));
    eb.append(tr);
  }
  const winRow = el('tr');
  const winCell = el('td', undefined, 'Win ');
  winCell.append(el('small', undefined, '\u00b7 100 pot'));
  winRow.append(winCell, el('td', 'w21-num', `+${num(ex.win)}`));
  const totalRow = el('tr', 'w21-sum w21-total');
  totalRow.append(el('td', undefined, 'Total, win included'), el('td', 'w21-num', num(ex.total + ex.win)));
  eb.append(winRow, totalRow);
  et.append(eb);
  exTable.append(et);

  const rankTable = el('div', 'w21-hiw-scroll');
  const rt = el('table', 'w21-odds-table w21-hiw-table');
  const rh = el('thead');
  const rhr = el('tr');
  rhr.append(el('th', undefined, 'Rank'), el('th', 'w21-num', 'Levels'), el('th', 'w21-num', 'From'), el('th', 'w21-num', 'Chest'));
  rh.append(rhr);
  const rb = el('tbody');
  ranks.forEach((r, i) => {
    const tr = el('tr', rankClass(rankFor(r.fromLevel).index));
    const first = el('td');
    const wrap = el('span', 'w21-hiw-rank');
    wrap.append(rankBadgeCard(rankFor(r.fromLevel), 22), el('span', undefined, r.name));
    first.append(wrap);
    const next = ranks[i + 1];
    const chest = el('td', 'w21-num');
    chest.append(rarityNames(RARITY_WORD[rankChest(i)]));
    tr.append(first, el('td', 'w21-num', next ? `${r.fromLevel}\u2013${next.fromLevel - 1}` : `${r.fromLevel}+`), el('td', 'w21-num', num(r.fromXp)), chest);
    rb.append(tr);
  });
  rt.append(rh, rb);
  rankTable.append(rt);
  const lastRank = ranks[ranks.length - 1]!.name;

  return section(
    'xp',
    'XP & ranks',
    lede('XP only grows. **Challenge games pay it**; solo practice pays none. The server works it out from its own replay of your game, never from anything the app sends.'),
    sub('What a game pays'),
    table(['Part', 'You get'], xpPartRows().map((r) => [r.label, r.rule])),
    p(`A round with no cards played pays nothing, not even the ${XP_RATES.played}. The win part arrives once both players have finished.`),
    sub('The example game above'),
    p(`${num(ex.round.score)} points, ${ex.round.clears} piles cleared, match level ${ex.round.level}, a ${streakLabelOf(ex.round.bestStreak)} best streak, played on a challenge (40 cards):`),
    exTable,
    p(`The pot counts up to ${XP_RATES.potCap} for the win part, so a bigger wager earns no extra XP.`),
    sub('Levels'),
    p(`Level n starts at ${XP_BASE} \u00d7 (n \u2212 1) \u00d7 (n + 3) XP, with no cap. Levels 1 to ${RANK_SPAN} are the first rank, and so on.`),
    table(['Level', 'Starts at', 'To next'], [[1, '0', num(xpThreshold(2))], ...xpLevelRows().map((r) => [r.level, `${num(r.xp)} XP`, num(r.cost)] as (string | number)[])]),
    sub('Ranks'),
    p(`One rank per ${RANK_SPAN} levels, with tiers I to ${'V'} inside it. Reaching a new rank earns a chest. Your rank is a playing card.`),
    rankTable,
    p(`After ${lastRank} the ladder keeps going: ${lastRank} II, ${lastRank} III and on, every ${RANK_SPAN} levels, each with a chest.`),
  );
}

function chests(): HTMLElement {
  const sources = sourceRows();
  const cards = el('div', 'w21-hiw-cards');
  for (const r of sources) {
    const card = el('div', 'w21-hiw-card');
    const head = el('div', 'w21-hiw-card-head');
    const starts = el('span', 'w21-hiw-starts');
    // Blockari's markup: <span><small>1st </small>Legendary</span> per start, the tag small and muted.
    for (const st of r.starts) {
      const one = el('span');
      if (st.label) one.append(el('small', undefined, `${st.label} `));
      one.append(rarityNames(st.rarity));
      starts.append(one);
    }
    head.append(el('b', undefined, r.label), starts);
    card.append(head, el('p', undefined, r.when));
    cards.append(card);
  }
  const finals = el('div', 'w21-hiw-scroll');
  const ft = el('table', 'w21-odds-table w21-hiw-table');
  const fh = el('thead');
  const fhr = el('tr');
  fhr.append(el('th'));
  for (const r of RARITIES) {
    const th = el('th', 'w21-num');
    th.append(rarityNames(RARITY_WORD[r]));
    fhr.append(th);
  }
  fh.append(fhr);
  const fb = el('tbody');
  for (const start of RARITIES) {
    const odds = finalOddsRow(start);
    const tr = el('tr');
    tr.append(el('td', undefined, RARITY_WORD[start]));
    for (const r of RARITIES) tr.append(el('td', 'w21-num', odds[r] === '0%' ? '\u2014' : odds[r]));
    fb.append(tr);
  }
  ft.append(fh, fb);
  finals.append(ft);

  const contents = el('div', 'w21-hiw-scroll');
  const ct = el('table', 'w21-odds-table w21-hiw-table');
  const ch = el('thead');
  const chr = el('tr');
  chr.append(el('th'));
  const rows = contentsTable();
  for (const c of rows) {
    const th = el('th', 'w21-num');
    th.append(rarityNames(RARITY_WORD[c.rarity]));
    chr.append(th);
  }
  ch.append(chr);
  const cb = el('tbody');
  const line = (label: string, small: string, pick: (c: (typeof rows)[number]) => string | StackedCell): void => {
    const tr = el('tr');
    const first = el('td', undefined, label);
    if (small) first.append(document.createTextNode(' '), el('small', undefined, `\u00b7 ${small}`));
    tr.append(first);
    for (const c of rows) {
      const v = pick(c);
      const td = el('td', 'w21-num');
      if (typeof v === 'string') td.textContent = v;
      else td.append(document.createTextNode(v.main), el('small', undefined, ` ${v.small}`));
      tr.append(td);
    }
    cb.append(tr);
  };
  line('$CHAIN', '', (c) => c.chain);
  line('Free entry', 'fee', (c) => c.ticket);
  line(`${BOOST.multiplier}\u00d7 XP`, 'games', (c) => c.boost);
  line('Cosmetic', '', (c) => c.cosmetic);
  ct.append(ch, cb);
  contents.append(ct);

  const upgrades = el('div', 'w21-hiw-scroll');
  const ut = el('table', 'w21-odds-table w21-hiw-table');
  const ub = el('tbody');
  for (const u of upgradeRows()) {
    const tr = el('tr');
    const first = el('td');
    first.append(rarityNames(RARITY_WORD[u.from]), document.createTextNode(' \u2192 '), rarityNames(RARITY_WORD[u.to]));
    tr.append(first, el('td', 'w21-num', u.chance));
    ub.append(tr);
  }
  ut.append(ub);
  upgrades.append(ut);

  const tag = el('span', 'w21-chip w21-mint', 'Earned \u00b7 never sold');
  return sectionWith(
    'chests',
    'Chests',
    tag,
    lede('Chests cannot be bought, with $CHAIN or anything else. You earn them, mostly by playing challenges. Each one is rolled the moment it is awarded; opening only reveals it.'),
    sub('How you earn one'),
    cards,
    p('Each milestone pays once, with no cooldown and no cap on how many you can earn. A guest earns chests too, but has to create an account before they expire to keep what is inside.'),
    sub('Rarity'),
    p('A chest starts at the rarity above. Each tap can upgrade it one step, stopping at the first miss:'),
    upgrades,
    p('Where a chest ends up, by the rarity it started as:'),
    finals,
    sub('What is inside'),
    contents,
    p('Every chest holds $CHAIN; the rest are chances. A free entry pays one challenge\u2019s entry at that rung. A cosmetic is a frame or a title you do not own yet.'),
  );
}

function bonus(): HTMLElement {
  const mine = CHESTS_ENABLED ? chestStore.getState().inventory : null;
  const live = mine && mine.boostGames > 0 ? mine : null;
  const lead = el('p', 'w21-hiw-lede');
  if (live) {
    lead.append(
      el('b', undefined, `${live.boostMultiplier}\u00d7 XP is on for ${live.boostGames} more ${live.boostGames === 1 ? 'game' : 'games'}.`),
      document.createTextNode(' You can see it in the top bar and on your profile.'),
    );
  } else {
    lead.textContent = 'You have no bonus right now. It comes out of chests, and shows in the top bar and on your profile while it lasts.';
  }
  const where = el('span');
  where.append(
    document.createTextNode('From chests only: the chance is in the contents table above. It cannot be bought. '),
    ...BOOST.gamesByRarity.flatMap((g) => [rarityNames(RARITY_WORD[g.rarity]), document.createTextNode(` gives ${g.games}. `)]),
  );
  return section(
    'bonus',
    `${BOOST.multiplier}\u00d7 XP bonus`,
    lead,
    qa([
      ['Where does it come from?', where],
      ['Does it switch on by itself?', 'Yes. Opening the chest adds it to your account, and the very next game that earns XP uses it. There is no button and no way to hold it back for a better game.'],
      ['Is it saved?', 'It stays on your account until used: it survives closing the app and signing in on another device, and it never runs out on a timer. A second one stacks, adding its games to the count.'],
      ['What counts as a use?', 'One finished challenge game that plays at least one card, however it ended. A game with no cards played, and solo practice, do not.'],
      ['What does it multiply?', `Everything that game pays (played, score, clears, levels, streak, challenge) by ${BOOST.multiplier}. Not the win part, which is paid later when the challenge settles. The results screen shows the doubled amounts, tagged with the multiplier.`],
    ]),
    p(`Free-entry tickets work the same way: kept until used, shown under Bonuses in Rewards. A ticket covers the entry at its own wager (10, 25, 50 or 100) and no other: switch it on when you create or take a challenge.`),
  );
}

function rings(onProfile?: () => void): HTMLElement {
  const demos: Array<[number, string]> = [[0.08, 'Just started'], [0.55, 'Halfway'], [1, 'Full: level up']];
  const figures = el('div', 'w21-hiw-rings');
  figures.setAttribute('aria-hidden', 'true');
  for (const [frac, label] of demos) {
    const figure = el('figure');
    const ring = el('span', 'w21-hiw-ring');
    const disc = el('span', `w21-avatar w21-lg w21-ringed w21-rank-${rankFor(12).index}`, '21');
    disc.append(avatarRing(frac));
    ring.append(disc);
    figure.append(ring, el('figcaption', undefined, label));
    figures.append(figure);
  }
  const back = el('p', 'w21-hiw-note');
  const link = el('a', undefined, 'your profile') as HTMLAnchorElement;
  link.href = '#profile';
  link.addEventListener('click', (e) => { e.preventDefault(); onProfile?.(); });
  back.append(document.createTextNode('Back to '), link, document.createTextNode('.'));
  return section(
    'rings',
    'Profile ring',
    lede('The ring around your initials (top bar and home) shows your progress to the next XP level.'),
    figures,
    qa([
      ['What does it measure?', 'XP earned inside your current level: empty when a level begins, full when you reach the next. It fills clockwise from the top, and the faint full circle is the whole level. The bar on your profile is the same number in text, with the XP still to go.'],
      ['What is its colour?', 'Your rank\u2019s colour. It changes when you reach a new rank.'],
      ['What happens when it fills?', `You level up and it starts again from empty. Every ${RANK_SPAN} levels a new rank begins: the ring sweeps once in the new colour and you earn a rank-up chest. Levels pay no $CHAIN.`],
      ['What is the card that flashes on screen?', 'Your rank. Ranks are playing cards, from the Deuce up to the Wild, and it shows when you reach a new one. It is also the badge on your profile.'],
      ['Is there another ring?', 'A frame you equip (Profile, Frames & titles) draws a plain decorative rim around the top-bar avatar. It shows no progress. The small dot on the avatar is unread notifications.'],
    ]),
    back,
  );
}

let profileTarget: (() => void) | undefined;

const SECTIONS: ReadonlyArray<{ id: string; label: string; build: () => HTMLElement }> = [
  { id: 'scoring', label: 'Scoring', build: scoring },
  { id: 'xp', label: 'XP & ranks', build: xp },
  { id: 'chests', label: 'Chests', build: chests },
  { id: 'bonus', label: '2\u00d7 XP', build: bonus },
  { id: 'rings', label: 'Profile ring', build: () => rings(profileTarget) },
];

/** The page itself: a scrolling screen with the shell bar and a strip of anchors. */
export class HowItWorksScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;
  readonly #fit: { measure(): void };
  #page: HTMLElement | null = null;

  constructor(callbacks: { bar?: TopBarCallbacks; onHome(): void; onHistory(): void; onProfile?(): void }) {
    const { screen, panel, fit } = createScreen('howitworks', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', '21 Wild — how it works');
    this.topBar = new TopBar(callbacks.bar ?? { onHome: () => callbacks.onHome(), onHistory: () => callbacks.onHistory() });
    screen.append(this.topBar.root);
    panel.className = 'w21-page';
    profileTarget = callbacks.onProfile;

    const page = el('div', 'w21-column w21-wide w21-hiw');
    const head = el('header', 'w21-hiw-head');
    head.append(
      el('h1', 'w21-t-title', 'How it works'),
      el('p', undefined, 'Every number on this page is read from the game\u2019s own rules, so it matches what you earn.'),
    );
    const toc = el('nav', 'w21-hiw-toc');
    toc.setAttribute('aria-label', 'On this page');
    const built = SECTIONS.map((s) => ({ s, node: s.build() }));
    const links = new Map<string, HTMLAnchorElement>();
    for (const { s: sec, node } of built) {
      const a = el('a', undefined, sec.label) as HTMLAnchorElement;
      a.href = `#${sec.id}`;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        light(sec.id);
      });
      links.set(sec.id, a);
      toc.append(a);
    }
    // The strip lights the section being read, and keeps the lit chip in view when it scrolls sideways.
    const light = (id: string): void => {
      for (const [k, a] of links) {
        a.classList.toggle('w21-on', k === id);
        if (k === id) { a.setAttribute('aria-current', 'true'); toc.scrollTo({ left: a.offsetLeft - (toc.clientWidth - a.offsetWidth) / 2, behavior: 'smooth' }); } else a.removeAttribute('aria-current');
      }
    };
    light(SECTIONS[0]!.id);
    const spy = (): void => {
      const line = this.root.getBoundingClientRect().top + 140;
      let best = built[0]!;
      for (const b of built) if (b.node.getBoundingClientRect().top <= line) best = b;
      light(best.s.id);
    };
    this.root.addEventListener('scroll', spy, { passive: true });
    page.append(head, toc, ...built.map((b) => b.node));
    this.#page = page;
    panel.append(page);
  }

  /** Back to the top whenever it is opened. */
  show(): void {
    this.root.scrollTop = 0;
    // The sticky strip sits under the bar: measure it so the strip and the anchors clear it.
    this.#page?.style.setProperty('--hiw-top', `${Math.round(this.topBar.root.getBoundingClientRect().height)}px`);
    this.#fit.measure();
  }
}
